"use client";

import clsx from "clsx";
import type { ComponentType } from "react";
import Link from "src/components/common/Link/Link";
import Tabs from "src/components/common/Tabs/Tabs";
import DocsCode from "./DocsCode";
import styles from "./RequestPreview/RequestPreview.module.scss";

/** A code block under a tab. */
export interface DocsTabCode {
  value: string;
  /** The highlighting language. */
  language: string;
  /** A follow-up block, set apart from the one above it. */
  companion?: boolean;
  /** An optional titled section under the shared selector. */
  heading?: { label: string; href: string };
}

export interface DocsCodeTab<T extends string> {
  value: T;
  label: string;
  icon: ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" }>;
}

export interface DocsCodeTabsProps<T extends string> {
  tabs: readonly DocsCodeTab<T>[];
  selected: T;
  /** Called with the chosen tab and its tab list, so the page can keep it in place. */
  onSelect: (value: T, tabList: HTMLElement | null) => void;
  /** The code under each tab. Only the selected tab's code is rendered. */
  code: Partial<Record<T, DocsTabCode[]>>;
  /** A link at the end of the tab bar. */
  link?: { href: string; label: string };
  className?: string;
  [dataAttribute: `data-${string}`]: string | undefined;
}

/**
 * The tab bar and code of a docs example. The choice it shows (an SDK language, a UI library) and
 * where it is remembered belong to the caller: DocsLanguageTabs, DocsUiLibraryTabs.
 */
const DocsCodeTabs = <T extends string>(props: DocsCodeTabsProps<T>) => {
  const { tabs, selected, onSelect, code, link, className, ...dataAttributes } = props;
  return (
    <Tabs
      value={selected}
      onValueChange={(value, details) => {
        const target = details.event.target;
        onSelect(
          value as T,
          target instanceof Element ? target.closest<HTMLElement>('[role="tablist"]') : null,
        );
      }}
      className={clsx(styles.root, className)}
      {...dataAttributes}
    >
      <div className={styles.tabBar}>
        <Tabs.List indicatorBar className={styles.tabList}>
          {tabs.map(({ value, label, icon: Icon }) => (
            <Tabs.Trigger key={value} value={value} className={styles.tab}>
              <Icon className="w-4 h-4" aria-hidden="true" />
              {label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {link ? (
          <Link href={link.href} variant="minimal" className={styles.tabLink}>
            {link.label}
          </Link>
        ) : null}
      </div>
      {tabs.map(({ value: tab }) => (
        <Tabs.Content key={tab} value={tab} className="m-0">
          {selected === tab &&
            code[tab]?.map((block, index) => {
              const editor = (
                <DocsCode
                  key={index}
                  value={block.value}
                  language={block.language}
                  fontSize={13}
                  softwrap={false}
                  className={block.companion ? styles.companion : styles.editor}
                />
              );
              return block.heading ? (
                <section
                  key={index}
                  className={styles.codeSection}
                  aria-label={block.heading.label}
                >
                  <Link href={block.heading.href} variant="minimal" className={styles.codeHeading}>
                    {block.heading.label}
                  </Link>
                  {editor}
                </section>
              ) : (
                editor
              );
            })}
        </Tabs.Content>
      ))}
    </Tabs>
  );
};

export default DocsCodeTabs;
